import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import ErrorMessages from './ErrorMessages';

type Labels = {
	addItem: string;
	removeItem: string;
};

type InputFieldComponentProps = {
	ariaDescribedBy?: string;
	ariaLabelledBy: string;
	id: string;
	name: string;
	onChange: (value: string) => void;
	onRemove: () => void;
	placeholder: string;
	shouldFocus?: boolean;
	text: Labels;
	type: string;
	value: string;
};

const TRANSITION = {
	duration: 0.25,
	animation: { ease: 'easeInOut' },
};

const InputField = ({
	ariaDescribedBy,
	ariaLabelledBy,
	id,
	name,
	onChange,
	onRemove,
	placeholder,
	shouldFocus,
	text,
	type,
	value,
}: InputFieldComponentProps) => {
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (shouldFocus && inputRef.current) {
			inputRef.current.focus();
		}
	}, [shouldFocus]);

	const variants = {
		hidden: {
			transform: 'translateX(-20px)',
			opacity: 0,
		},
		visible: {
			transform: 'translateX(0)',
			opacity: 1,
		},
	};

	return (
		<motion.div initial="hidden" animate="visible" exit="hidden" variants={variants} transition={TRANSITION} className="input-group__entry">
			<input
				ref={inputRef}
				type={type}
				id={id}
				name={name}
				placeholder={placeholder}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				aria-labelledby={ariaLabelledBy}
				{...(ariaDescribedBy ? { 'aria-describedby': ariaDescribedBy } : {})}
			/>
			<button type="button" className="input-group__remove-item" title={text.removeItem} aria-label={text.removeItem} onClick={onRemove} />
		</motion.div>
	);
};

type InputGroupProps = {
	description?: string;
	errorMessages?: string[];
	label: string;
	maxItems?: number | null;
	name: string;
	onChange: (value: string[]) => void;
	placeholder?: string;
	text: Labels;
	type?: string;
	value: string[];
};

export default function Input({
	description = '',
	errorMessages = [],
	label,
	placeholder = '',
	maxItems = null,
	name,
	onChange,
	text,
	type = 'text',
	value,
}: InputGroupProps) {
	const [lastAddedIndex, setLastAddedIndex] = useState<number | null>(null);

	const addInput = () => {
		if (maxItems === null || value.length < maxItems) {
			const newIndex = value.length;
			onChange([...value, '']);
			setLastAddedIndex(newIndex);
		}
	};

	const removeInput = (index: number) => {
		onChange(value.filter((_, i) => i !== index));
		setLastAddedIndex(null);
	};

	const updateValue = (index: number, newValue: string) => {
		const newValues = [...value];
		newValues[index] = newValue;
		onChange(newValues);
	};

	return (
		<div className="form-field-wrapper">
			<div className="form-field input-group" data-field-type="input-group" data-field-name={name}>
				<label id={`${name}-label`} htmlFor={`${name}-0`}>
					{label}
					{maxItems !== null ? ` (${value.length} / ${maxItems})` : ''}
				</label>
				<div className="input-group__container" role="group" aria-labelledby={`${name}-label`}>
					{description && <p id={`${name}-description`}>{description}</p>}
					<div className="input-group__entries">
						<AnimatePresence>
							{value.map((item, index) => (
								<InputField
									ariaDescribedBy={description ? `${name}-description` : undefined}
									ariaLabelledBy={`${name}-label`}
									id={`${name}-${index}`}
									key={index}
									name={`${name}[]`}
									onChange={(newValue) => updateValue(index, newValue)}
									onRemove={() => removeInput(index)}
									placeholder={placeholder}
									shouldFocus={index === lastAddedIndex}
									text={text}
									type={type}
									value={item}
								/>
							))}
						</AnimatePresence>
					</div>
					{!maxItems ||
						(value.length < maxItems && (
							<motion.button
								type="button"
								className="input-group__add-item"
								onClick={addInput}
								transition={TRANSITION}
								initial="hidden"
								animate="visible"
								variants={{
									hidden: { opacity: 0 },
									visible: { opacity: 1 },
								}}
							>
								{text.addItem}
							</motion.button>
						))}
				</div>
			</div>

			<ErrorMessages messages={errorMessages} />
		</div>
	);
}
